import { useTheme } from '@mui/material/styles';
import React, { useState, useEffect } from 'react';
import moment from 'moment';

import {
  Grid,
  Typography,
  Alert,
  Backdrop,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// project imports
import axios from 'src/utils/axios';

// ==============================|| PROFILE 1 - KOÇ AİLEM UYGUNLUK ||============================== //

const styles = {
  backdrop: (theme) => ({
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff',
  }),
};

const formatTarih = (value) => (value ? moment(value).format('DD.MM.YYYY') : '-');

// API bit alanlarını true/false, 1/0 ya da "1"/"0" string olarak dönebiliyor; hepsini normalize eder
const toBool = (value) => value === true || value === 1 || value === '1' || value === 'true';

const KocAilemUygunluk = ({ EmployeeNr }) => {
  const theme = useTheme();

  if (EmployeeNr === 0) {
    return (
      <Grid container spacing={2}>
        <Grid
          size={{
            xs: 12,
            sm: 12,
            md: 12
          }}>
          <Alert variant="outlined" severity="error" sx={{ borderColor: theme.palette.warning.dark }}>
            <Typography variant='h5' style={{ color: 'red' }}>Önce Kayıt İşlemini Tamamlamanız Gerekli!....
            </Typography>
          </Alert>      </Grid>
      </Grid>
    );
  }

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`GetKocAilemUygunluk/${EmployeeNr}`).then((response) => {
      setData(response?.data || null);
      setLoading(false);
    }).catch(() => {
      setData(null);
      setLoading(false);
    });
  }, [EmployeeNr]);

  // Bayi sahibi rollerinde (Bayi Sahibi/Ortak, Bayi Sahibi/Yönetici Ortak) robottan
  // evrak kontrolü şartı aranmaz; kıdem ve doküman tarihi kuralları bu rollerde
  // bilgi amaçlı gösterilir, üyelik başlatmayı engellemez.
  const evrakKontrolMuaf = toBool(data?.EvrakKontrolMuafMi);

  // Yalnızca robot evrak kontrolünden gelen şartlar. "TC başka kayıtta aktif" bilinçli
  // olarak burada değil: o bir kural ihlali değil, "kişi zaten üye" durum bilgisidir.
  const kurallar = data ? [
    {
      key: 'kidem',
      baslik: 'Kıdem Şartı',
      aciklama: 'SGK döküm tarihi ile işe giriş tarihi arasında en az 180 gün olmalıdır.',
      sorunVarMi: toBool(data.KidemSorunuVarMi),
      detay: data.KidemGunSayisi !== null && data.KidemGunSayisi !== undefined
        ? `Mevcut kıdem: ${data.KidemGunSayisi} gün (gerekli: 180 gün)`
        : 'Hesaplanamadı'
    },
    {
      key: 'dokumanTarih',
      baslik: 'Doküman Tarih Şartı',
      aciklama: 'Barkodlu SGK hizmet dökümü, sisteme yüklenip kontrol edildikten sonra en fazla 30 gün geçerlidir.',
      sorunVarMi: toBool(data.DokumanTarihSorunuVarMi),
      detay: data.DokumanKontrolGecikmeGunSayisi !== null && data.DokumanKontrolGecikmeGunSayisi !== undefined
        ? `Son kontrolün üzerinden ${data.DokumanKontrolGecikmeGunSayisi} gün geçti (izin verilen: 30 gün)`
        : 'Hesaplanamadı'
    }
  ] : [];

  const kocAilemAktif = toBool(data?.KocAilemAktif);
  const uyelikBaslatilabilirMi = toBool(data?.UyelikBaslatilabilirMi);
  // Üyelik TC bazlıdır: kişinin kendi kaydı pasif olsa da aynı TC'li başka bir kayıttan
  // aktif olabilir. Bu alan SP güncellenmeden önce gelmezse (undefined) false olur.
  const aktifBaskaKayittan = toBool(data?.KocAilemAktifBaskaKayittanMi);
  const tcBaskaKayittaAktif = toBool(data?.TcBaskaBayideAktifVarMi);
  const aktifAmaSorunVar = kocAilemAktif && !evrakKontrolMuaf && kurallar.some((k) => k.sorunVarMi);

  return (
    <div>
      {
        loading ?
          <Backdrop sx={styles.backdrop} open={loading}>
            <CircularProgress color="inherit" />
          </Backdrop>
          : <div></div>
      }
      <Grid container spacing={2}>
        {!loading && data && !toBool(data.KayitBulunduMu) ? (
          <Grid size={12}>
            <Alert severity="info">
              Bu çalışan için Koç Ailem / SGK Hizmet Dökümü kaydı bulunamadı.
            </Alert>
            {/* Muaf rollerde robot kaydı hiç olmayabilir; bu durumda süreç yine de
                başlatılabilir. Aksi halde ekran "kayıt yok" derken başlıktaki
                Koç Ailem butonunun görünmesi tutarsız görünürdü. */}
            {evrakKontrolMuaf ? (
              <Alert severity="info" style={{ marginTop: 12 }}>
                Bu çalışanın görevi (Bayi Sahibi/Ortak veya Bayi Sahibi/Yönetici Ortak) için robottan evrak kontrolü şartı aranmaz. SGK hizmet dökümü kaydı bulunmasa da Koç Ailem süreci başlatılabilir.
              </Alert>
            ) : null}
          </Grid>
        ) : null}
        {!loading && data && toBool(data.KayitBulunduMu) ? (
          <>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Typography variant="h5" sx={{ mb: 1 }}>Koç Ailem - Üyelik Durumu</Typography>
              <Divider sx={{ mb: 2 }} />
                <Grid container spacing={1} style={{ marginBottom: 12 }}>
                  <Grid>
                    <Chip
                      label={kocAilemAktif ? 'Üyelik: Aktif' : 'Üyelik: Pasif'}
                      style={{
                        backgroundColor: kocAilemAktif ? '#00C853' : '#9e9e9e',
                        color: 'white'
                      }}
                    />
                  </Grid>
                  {!kocAilemAktif ? (
                    <Grid>
                      <Chip
                        label={uyelikBaslatilabilirMi ? 'Başlatılabilir' : 'Başlatılamaz'}
                        style={{
                          backgroundColor: uyelikBaslatilabilirMi ? '#00C853' : '#D84315',
                          color: 'white'
                        }}
                      />
                    </Grid>
                  ) : null}
                </Grid>
                {aktifBaskaKayittan ? (
                  <Alert severity="info" style={{ marginBottom: 12 }}>
                    Üyelik bu kayıt üzerinden değil, aynı TC kimlik numarasına ait başka bir çalışan kaydı üzerinden aktiftir. Koç Ailem üyeliği TC bazlıdır; tekrar aktiflemeye gerek yoktur.
                  </Alert>
                ) : null}
                {evrakKontrolMuaf ? (
                  <Alert severity="info" style={{ marginBottom: 12 }}>
                    Bu çalışanın görevi (Bayi Sahibi/Ortak veya Bayi Sahibi/Yönetici Ortak) için robottan evrak kontrolü şartı aranmaz. Aşağıdaki kurallar yalnızca bilgi amaçlıdır.
                  </Alert>
                ) : null}
                {aktifAmaSorunVar ? (
                  <Alert severity="warning" icon={<WarningAmberIcon />} style={{ marginBottom: 12 }}>
                    Çalışan hâlihazırda aktif Koç Ailem üyesi, ancak güncel verilere göre aşağıdaki kural(lar) artık sağlanmıyor. Gözden geçirilmesi önerilir.
                  </Alert>
                ) : null}
                <List dense>
                  {kurallar.map((kural) => (
                    <ListItem key={kural.key} alignItems="flex-start">
                      <ListItemIcon>
                        {/* Muaf rollerde kural sağlanmasa bile engel değildir; kırmızı (x)
                            yerine nötr bilgi ikonu gösterilir. */}
                        {evrakKontrolMuaf ? (
                          <InfoOutlinedIcon style={{ color: '#9e9e9e' }} />
                        ) : kural.sorunVarMi ? (
                          <CancelIcon style={{ color: '#D84315' }} />
                        ) : (
                          <CheckCircleIcon style={{ color: '#00C853' }} />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={evrakKontrolMuaf ? `${kural.baslik} (aranmıyor)` : kural.baslik}
                        secondary={
                          <>
                            <Typography component="span" variant="body2" display="block">{kural.aciklama}</Typography>
                            <Typography component="span" variant="body2" display="block" color="textSecondary">{kural.detay}</Typography>
                          </>
                        }
                      />
                    </ListItem>
                  ))}
                  {/* TC bazlı üyelik durumu: kural ihlali değil, bilgi satırıdır. */}
                  <ListItem alignItems="flex-start">
                    <ListItemIcon>
                      <InfoOutlinedIcon style={{ color: tcBaskaKayittaAktif ? '#0288D1' : '#9e9e9e' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Aynı TC ile Başka Kayıtta Üyelik"
                      secondary={
                        <>
                          <Typography component="span" variant="body2" display="block">
                            Koç Ailem üyeliği TC bazlıdır; bir TC için tek üyelik açılır.
                          </Typography>
                          <Typography component="span" variant="body2" display="block" color="textSecondary">
                            {tcBaskaKayittaAktif
                              ? 'Aynı TC ile başka bir çalışan kaydında aktif üyelik bulundu'
                              : 'Aynı TC ile başka bir kayıtta aktif üyelik yok'}
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                </List>
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}
              sx={{
                borderLeft: { md: '1px solid' },
                borderColor: 'divider',
                pl: { md: 4 }
              }}>
              <Typography variant="h5" sx={{ mb: 1 }}>Koç Ailem - Tarih Bilgileri</Typography>
              <Divider sx={{ mb: 2 }} />
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="Doküman Kontrol Tarihi"
                      primaryTypographyProps={{ style: { fontWeight: 700, color: theme.palette.text.primary } }}
                      secondary={formatTarih(data.DokumanKontrolTarihi)}
                    />
                  </ListItem>
                  <Divider component="li" />
                  <ListItem>
                    <ListItemText
                      primary="SGK Döküm Tarihi"
                      primaryTypographyProps={{ style: { fontWeight: 700, color: theme.palette.text.primary } }}
                      secondary={formatTarih(data.SgkDokumTarihi)}
                    />
                  </ListItem>
                  <Divider component="li" />
                  <ListItem>
                    <ListItemText
                      primary="İşe Giriş Tarihi"
                      primaryTypographyProps={{ style: { fontWeight: 700, color: theme.palette.text.primary } }}
                      secondary={formatTarih(data.IseGirisTarihi)}
                    />
                  </ListItem>
                  <Divider component="li" />
                  <ListItem>
                    <ListItemText
                      primary="Doğum Tarihi"
                      primaryTypographyProps={{ style: { fontWeight: 700, color: theme.palette.text.primary } }}
                      secondary={formatTarih(data.DogumTarihi)}
                    />
                  </ListItem>
                </List>
            </Grid>
          </>
        ) : null}
      </Grid>
    </div>
  );
};

export default KocAilemUygunluk;
